import { useCallback, useId, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { resolveComponentVariant } from '../componentContract'
import { IconButton } from '../primitives/IconButton'
import { OverlayPortal, useOverlayLifecycle } from './OverlayFoundation'
import styles from './Overlays.module.css'

export function Drawer({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  side = 'left',
  dismissable = true,
  initialFocusRef,
  returnFocusRef,
  returnFocusFallbackRef,
  className = '',
  inline = false,
}) {
  const surfaceRef = useRef(null)
  const portalHostRef = useRef(null)
  const inlineSlotRef = useRef(null)
  const modalBackdropRef = useRef(null)
  const modalSlotRef = useRef(null)
  if (portalHostRef.current === null && typeof document !== 'undefined') {
    portalHostRef.current = document.createElement('div')
    portalHostRef.current.style.display = 'contents'
    portalHostRef.current.dataset.drawerHost = ''
  }
  const titleId = useId()
  const descriptionId = useId()
  const resolvedSide = resolveComponentVariant('Drawer side', side, ['left', 'right'], 'left')
  const transitionStateRef = useRef({
    mode: null,
    scrollTop: 0,
    focus: null,
    disclosures: [],
  })
  const preserveTransitionFocusRef = useRef(false)
  preserveTransitionFocusRef.current = (
    open
    && transitionStateRef.current.mode !== null
    && transitionStateRef.current.mode !== inline
  )
  // The surface always renders into one stable portal host. Breakpoint
  // changes move that host between modal and inline slots without remounting
  // the inspector subtree, so field state and native disclosure/scroll state
  // retain one React owner.
  useLayoutEffect(() => {
    const host = portalHostRef.current
    if (!open) {
      host?.remove()
      return
    }
    const target = inline ? inlineSlotRef.current : modalSlotRef.current
    if (host && target && host.parentNode !== target) target.append(host)
    if (modalBackdropRef.current) modalBackdropRef.current.hidden = inline
  }, [inline, open])
  useLayoutEffect(() => {
    const surface = surfaceRef.current
    if (!surface || !open || !inline) {
      surface?.style.removeProperty('--wf-drawer-inline-available-height')
      return undefined
    }
    const syncAvailableHeight = () => {
      const top = Math.max(0, surface.getBoundingClientRect().top)
      const rootStyle = window.getComputedStyle(document.documentElement)
      const pageGutter = Number.parseFloat(rootStyle.getPropertyValue('--wf-page-gutter')) || 0
      const nextHeight = `${Math.max(0, window.innerHeight - top - pageGutter)}px`
      if (surface.style.getPropertyValue('--wf-drawer-inline-available-height') !== nextHeight) {
        surface.style.setProperty('--wf-drawer-inline-available-height', nextHeight)
      }
    }
    syncAvailableHeight()
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(syncAvailableHeight)
    let ancestor = surface.parentElement
    while (ancestor && observer) {
      observer.observe(ancestor)
      ancestor = ancestor.parentElement
    }
    window.addEventListener('resize', syncAvailableHeight)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', syncAvailableHeight)
      surface.style.removeProperty('--wf-drawer-inline-available-height')
    }
  }, [inline, open])
  const captureLayoutState = useCallback(() => {
    const surface = surfaceRef.current
    if (!surface) return
    const content = surface.querySelector('[data-drawer-content]')
    const focusable = [...surface.querySelectorAll(
      'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])',
    )]
    const active = surface.contains(document.activeElement) ? document.activeElement : null
    transitionStateRef.current = {
      mode: inline,
      scrollTop: content?.scrollTop || 0,
      focus: active ? {
        tagName: active.tagName,
        name: active.getAttribute('name') || '',
        ariaLabel: active.getAttribute('aria-label') || '',
        index: focusable.indexOf(active),
      } : null,
      disclosures: [...surface.querySelectorAll('details')].map((details) => details.open),
    }
  }, [inline])
  useLayoutEffect(() => {
    if (!open) {
      transitionStateRef.current = {
        mode: null,
        scrollTop: 0,
        focus: null,
        disclosures: [],
      }
      return undefined
    }
    const saved = transitionStateRef.current
    const surface = surfaceRef.current
    if (surface && saved.mode !== null && saved.mode !== inline) {
      const content = surface.querySelector('[data-drawer-content]')
      if (content) content.scrollTop = saved.scrollTop
      const disclosures = [...surface.querySelectorAll('details')]
      saved.disclosures.forEach((expanded, index) => {
        if (disclosures[index]) disclosures[index].open = expanded
      })
      let focusTarget = null
      if (saved.focus) {
        const focusable = [...surface.querySelectorAll(
          'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])',
        )]
        const exact = focusable.find((element) => (
          element.tagName === saved.focus.tagName
          && (element.getAttribute('name') || '') === saved.focus.name
          && (element.getAttribute('aria-label') || '') === saved.focus.ariaLabel
        ))
        focusTarget = exact || focusable[saved.focus.index]
      }
      // The portal handoff and modal cleanup finish after this layout effect.
      // Reapply scroll, then focus, in the following microtask so neither the
      // ownership move nor return-focus cleanup overwrites the saved state.
      window.queueMicrotask(() => {
        if (transitionStateRef.current.mode !== inline) return
        if (content?.isConnected) content.scrollTop = saved.scrollTop
        if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true })
      })
    }
    transitionStateRef.current.mode = inline
    return captureLayoutState
  }, [captureLayoutState, inline, open])
  useOverlayLifecycle({
    open: open && !inline,
    onClose,
    dismissable,
    modal: !inline,
    trapFocus: !inline,
    surfaceRef,
    initialFocusRef,
    returnFocusRef,
    returnFocusFallbackRef,
    preserveFocusRef: preserveTransitionFocusRef,
  })
  if (!open) return null

  const surface = (
    <aside
      ref={surfaceRef}
      className={`${styles.surface} ${styles.drawer} ${resolvedSide === 'left' ? styles.drawerLeft : styles.drawerRight} ${inline ? styles.drawerInline : ''} ${className}`.trim()}
      role={inline ? 'region' : 'dialog'}
      aria-modal={inline ? undefined : 'true'}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      tabIndex={-1}
    >
      <header className={styles.header}>
        <div className={styles.heading}>
          {title ? <h2 id={titleId} className={styles.title}>{title}</h2> : null}
          {description ? <p id={descriptionId} className={styles.description}>{description}</p> : null}
        </div>
        <IconButton variant="ghost" label="Close drawer" onClick={onClose}><X size={18} aria-hidden="true" /></IconButton>
      </header>
      <div className={styles.content} data-drawer-content>{children}</div>
      {footer ? <footer className={styles.footer}>{footer}</footer> : null}
    </aside>
  )

  const surfacePortal = portalHostRef.current
    ? createPortal(surface, portalHostRef.current, 'drawer-surface')
    : null
  return (
    <>
      <div ref={inlineSlotRef} className={styles.drawerSlot} />
      <OverlayPortal>
        <div
          ref={modalBackdropRef}
          className={`${styles.backdrop} ${styles.drawerBackdrop}`}
          onMouseDown={(event) => { if (dismissable && event.target === event.currentTarget) onClose?.() }}
        >
          <div ref={modalSlotRef} className={styles.drawerSlot} />
        </div>
      </OverlayPortal>
      {surfacePortal}
    </>
  )
}
